import {
    applyRules,
    ruleBreakdown,
    rulesForVersion,
    type PricedProduct,
    type PricedVersion
} from '../../../services/practiceProductPricing';
import { formatMoneyCents } from '../../../utils/money';

// Breakdown of how a product's effective price for this practice is built:
// the version's list price minus each stacked discount rule the practice is
// eligible for (percentages compound first, then flat amounts, floored at $0
// - same order as the backend's applyRules). Uses the resolved version by
// default; pass `version` explicitly for unresolved products' per-version rows.
//
// A plain panel, not a `colSpan` table row: it renders inside the Product
// column's own cell (DataTable has no row-spanning-row concept), so its width
// is that column's, not the whole table's.

function ruleAmountLabel(discountType: string, discountValue: number): string {
    return discountType === 'percentage'
        ? `-${discountValue}%`
        : `-${formatMoneyCents(Math.round(discountValue * 100))}`;
}

interface PricingBreakdownPanelProps {
    product: PricedProduct;
    practiceName: string;
    version?: PricedVersion;
}

export function PricingBreakdownPanel({
    product,
    practiceName,
    version
}: PricingBreakdownPanelProps) {
    const versionId = version?.id ?? product.resolvedVersionId;
    const applied = versionId ? rulesForVersion(product.rules, versionId) : [];
    const listCents = version ? version.priceCents : product.defaultPriceCents;
    const effectiveCents = version
        ? applyRules(version.priceCents, applied)
        : product.effectivePriceCents;

    return (
        <div className="mt-2 max-w-md rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5">
            {listCents === null || effectiveCents === null ? (
                <p className="text-xs text-gray-500">
                    No version of this product resolves for {practiceName} - discounts apply once a
                    version is picked at charge time.
                </p>
            ) : applied.length === 0 ? (
                <p className="text-xs text-gray-500">No discount rules apply - list price.</p>
            ) : (
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">List price</span>
                        <span className="text-gray-700 tabular-nums">
                            {formatMoneyCents(listCents)}
                        </span>
                    </div>
                    {ruleBreakdown(listCents, applied).map(({ rule, deltaCents }) => (
                        <div
                            key={rule.id}
                            className="flex items-center justify-between text-xs gap-3"
                        >
                            <span className="min-w-0 flex items-center gap-2">
                                <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium bg-green-50 text-green-600 shrink-0">
                                    {ruleAmountLabel(rule.discountType, rule.discountValue)}
                                </span>
                                <span className="text-gray-600 truncate" title={rule.name}>
                                    {rule.name}
                                </span>
                            </span>
                            <span className="text-gray-700 tabular-nums shrink-0">
                                -{formatMoneyCents(deltaCents)}
                            </span>
                        </div>
                    ))}
                    <div className="flex items-center justify-between text-xs border-t border-gray-200 pt-1.5">
                        <span className="font-semibold text-gray-800">Effective price</span>
                        <span className="font-semibold text-gray-900 tabular-nums">
                            {formatMoneyCents(effectiveCents)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
